import axios from "axios";
import {fetchAuthSession, signOut} from "aws-amplify/auth";

const dupeBusinessClient = axios.create({
    baseURL: (import.meta.env.VITE_REACT_APP_BACKEND_ENDPOINT || process.env.REACT_APP_BACKEND_ENDPOINT)
});

// Attach the id token when we have one. An unauthenticated visitor still gets
// their request sent (without the header) so the API can answer 401 and the
// caller can route them somewhere useful. Signing out from here instead --
// while leaving the request promise unsettled -- stranded every logged-out
// visitor, most visibly the invitees landing on /join/:inviteId.
dupeBusinessClient.interceptors.request.use(async function (config) {
    try {
        const session = await fetchAuthSession();
        const idToken = session?.tokens?.idToken;
        if (idToken) {
            config.headers.set('X-CLIENT-ID', idToken.toString());
        }
    } catch (error) {
        // No logged-in user: send the request unauthenticated.
    }
    return config;
});

async function hasValidSession() {
    try {
        const session = await fetchAuthSession();
        return Boolean(session?.tokens?.idToken);
    } catch (error) {
        return false;
    }
}

function emitPaymentRequired() {
    window.dispatchEvent(new CustomEvent('paymentRequired', { detail: { status: 402 } }));
}

dupeBusinessClient.interceptors.response.use(response => {
    if (response.status === 402) {
        emitPaymentRequired();
    }
    return response;
}, async error => {
    const status = error.response?.status;
    if (status === 402) {
        emitPaymentRequired();
    }
    if (status === 401 && !(await hasValidSession())) {
        // Stale or absent credentials. Clearing them lets the app's auth
        // listener route to login. Guarded on the session actually being gone
        // so an authorization 401 can't sign out a perfectly good session.
        await signOut().catch(() => {});
    }
    return Promise.reject(error);
})

// Helpful Debugging tools
// dupeClient.interceptors.request.use(request => {
//     console.log('Starting Request', JSON.stringify(request, null, 2))
//     return request
// })
//
// dupeClient.interceptors.response.use(response => {
//     console.log('Response:', JSON.stringify(response, null, 2))
//     return response
// })


export default dupeBusinessClient;